import React, { useEffect, useState } from 'react';
import './index.css';

// YouTube video ID for "How to create your Deriv account" tutorial (replace with your video ID, or leave empty for placeholder)
const ACCOUNT_TUTORIAL_VIDEO_ID = '';

// Nephtech copyright – WhatsApp contact (254706594653)
const NEPHTECH_WHATSAPP_URL = 'https://wa.me/254706594653';

// Track if script is loaded globally (outside component)
let scriptLoaded = false;

function App() {
  const [activeMobilePage, setActiveMobilePage] = useState('chart');
  const [activeRightNavView, setActiveRightNavView] = useState('chart'); // desktop right nav: chart | signals | community

  useEffect(() => {
    // Only load script once
    if (scriptLoaded || window.__TRADING_SCRIPT_LOADED__) {
      return;
    }
    scriptLoaded = true;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const script = document.createElement('script');
      script.src = '/trading.js';
      script.async = false;
      document.body.appendChild(script);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Sync mobile tables when page changes
  useEffect(() => {
    if (!window.updateOpenContractsTable || !window.updateContractHistoryTable) {
      return;
    }

    // Update tables when switching to trades or history pages
    if (activeMobilePage === 'trades') {
      setTimeout(() => {
        if (window.updateOpenContractsTable) {
          window.updateOpenContractsTable();
        }
      }, 100);
    } else if (activeMobilePage === 'history') {
      setTimeout(() => {
        if (window.updateContractHistoryTable) {
          window.updateContractHistoryTable();
        }
        if (window.updateTotalPL) {
          window.updateTotalPL();
        }
      }, 100);
    }
  }, [activeMobilePage]);

  // Sync mobile settings with desktop settings
  useEffect(() => {
    const syncSettings = () => {
      // Sync account toggle
      const desktopToggle = document.getElementById('accountToggleInput');
      const mobileToggle = document.getElementById('mobileAccountToggleInput');
      if (desktopToggle && mobileToggle) {
        mobileToggle.checked = desktopToggle.checked;
        mobileToggle.addEventListener('change', (e) => {
          if (desktopToggle) desktopToggle.checked = e.target.checked;
          if (desktopToggle) desktopToggle.dispatchEvent(new Event('change'));
        });
      }

      // Sync theme toggle
      const mobileThemeToggle = document.getElementById('mobileThemeToggle');
      const themeToggle = document.getElementById('themeToggle');
      if (mobileThemeToggle) {
        const isDark = document.documentElement.classList.contains('dark-theme');
        mobileThemeToggle.checked = isDark;
        mobileThemeToggle.addEventListener('change', (e) => {
          if (themeToggle) themeToggle.click();
        });
      }

      // Sync color pickers
      const desktopBullish = document.getElementById('bullishColorPicker');
      const mobileBullish = document.getElementById('mobileBullishColorPicker');
      if (desktopBullish && mobileBullish) {
        mobileBullish.value = desktopBullish.value;
        mobileBullish.addEventListener('input', (e) => {
          desktopBullish.value = e.target.value;
          desktopBullish.dispatchEvent(new Event('input'));
        });
      }

      const desktopBearish = document.getElementById('bearishColorPicker');
      const mobileBearish = document.getElementById('mobileBearishColorPicker');
      if (desktopBearish && mobileBearish) {
        mobileBearish.value = desktopBearish.value;
        mobileBearish.addEventListener('input', (e) => {
          desktopBearish.value = e.target.value;
          desktopBearish.dispatchEvent(new Event('input'));
        });
      }

      const desktopMa5 = document.getElementById('ma5ColorPicker');
      const mobileMa5 = document.getElementById('mobileMa5ColorPicker');
      if (desktopMa5 && mobileMa5) {
        mobileMa5.value = desktopMa5.value;
        mobileMa5.addEventListener('input', (e) => {
          desktopMa5.value = e.target.value;
          desktopMa5.dispatchEvent(new Event('input'));
        });
      }

      // Sync MA5 toggle
      const desktopMa5Toggle = document.getElementById('ma5ToggleBtn');
      const mobileMa5Toggle = document.getElementById('mobileMa5Toggle');
      if (desktopMa5Toggle && mobileMa5Toggle) {
        mobileMa5Toggle.checked = desktopMa5Toggle.getAttribute('data-active') === 'true';
        mobileMa5Toggle.addEventListener('change', (e) => {
          if (desktopMa5Toggle) desktopMa5Toggle.click();
        });
      }

      // Sync trading history toggle
      const desktopHistoryToggle = document.getElementById('tradingHistoryToggle');
      const mobileHistoryToggle = document.getElementById('mobileTradingHistoryToggle');
      if (desktopHistoryToggle && mobileHistoryToggle) {
        mobileHistoryToggle.checked = desktopHistoryToggle.checked;
        mobileHistoryToggle.addEventListener('change', (e) => {
          desktopHistoryToggle.checked = e.target.checked;
          desktopHistoryToggle.dispatchEvent(new Event('change'));
        });
      }

      // Sync balance display
      const desktopBalance = document.getElementById('balanceDisplay');
      const mobileBalance = document.getElementById('mobileBalanceDisplay');
      if (desktopBalance && mobileBalance) {
        const updateBalance = () => {
          mobileBalance.textContent = desktopBalance.textContent;
        };
        updateBalance();
        // Watch for balance updates
        const observer = new MutationObserver(updateBalance);
        observer.observe(desktopBalance, { childList: true, characterData: true, subtree: true });
      }

      // Sync account label
      const desktopAccountLabel = document.getElementById('accountToggleLabel');
      const mobileAccountLabel = document.getElementById('mobileAccountToggleLabel');
      if (desktopAccountLabel && mobileAccountLabel) {
        const updateLabel = () => {
          mobileAccountLabel.textContent = desktopAccountLabel.textContent;
        };
        updateLabel();
        const observer = new MutationObserver(updateLabel);
        observer.observe(desktopAccountLabel, { childList: true, characterData: true, subtree: true });
      }

      // Sync login button
      const desktopLoginBtn = document.getElementById('loginBtn');
      const mobileLoginBtn = document.getElementById('mobileLoginBtn');
      if (desktopLoginBtn && mobileLoginBtn) {
        mobileLoginBtn.addEventListener('click', () => {
          desktopLoginBtn.click();
        });
      }

      // Sync clear history button
      const desktopClearBtn = document.getElementById('clearHistoryBtn');
      const mobileClearBtn = document.getElementById('mobileClearHistoryBtn');
      if (desktopClearBtn && mobileClearBtn) {
        mobileClearBtn.addEventListener('click', () => {
          desktopClearBtn.click();
        });
      }
    };

    // Wait for trading script to load
    const checkScript = setInterval(() => {
      if (window.__TRADING_SCRIPT_LOADED__) {
        clearInterval(checkScript);
        setTimeout(syncSettings, 500);
      }
    }, 100);

    return () => clearInterval(checkScript);
  }, []);

  return (
    <>
      {/* Abstract Golden Loader */}
      <div id="loader-wrapper">
        <div className="abstract-loader">
          {/* Flowing Golden Waves */}
          <div className="wave-container">
            <svg className="wave wave-1" viewBox="0 0 1200 300" preserveAspectRatio="none">
              <path d="M0,100 Q300,50 600,100 T1200,100 L1200,300 L0,300 Z" />
            </svg>
            <svg className="wave wave-2" viewBox="0 0 1200 300" preserveAspectRatio="none">
              <path d="M0,150 Q300,100 600,150 T1200,150 L1200,300 L0,300 Z" />
            </svg>
            <svg className="wave wave-3" viewBox="0 0 1200 300" preserveAspectRatio="none">
              <path d="M0,200 Q300,150 600,200 T1200,200 L1200,300 L0,300 Z" />
            </svg>
          </div>

          {/* Floating Golden Particles */}
          <div className="particle-system">
            {[...Array(30)].map((_, i) => (
              <div key={i} className="golden-particle" style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${5 + Math.random() * 10}s`
              }}></div>
            ))}
          </div>

          {/* Geometric Shapes */}
          <div className="geometric-shapes">
            <div className="shape hexagon"></div>
            <div className="shape triangle"></div>
            <div className="shape circle"></div>
          </div>

          {/* Main Content */}
          <div className="loader-main">
            <div className="logo-ring-container">
              <div className="golden-ring ring-outer"></div>
              <div className="golden-ring ring-middle"></div>
              <div className="golden-ring ring-inner"></div>
              <img src="/logo/logo.png" alt="gletradershub.com" className="abstract-logo" />
            </div>
            
            <h1 className="abstract-brand">gletradershub.com</h1>
            <p className="abstract-tagline">PROFESSIONAL TRADING PLATFORM</p>
            
            <div className="loading-bar-abstract">
              <div className="bar-track">
                <div className="bar-fill"></div>
                <div className="bar-shimmer"></div>
              </div>
            </div>
          </div>

          {/* Light Beams */}
          <div className="light-beams">
            <div className="beam"></div>
            <div className="beam"></div>
            <div className="beam"></div>
          </div>
        </div>
      </div>

      {/* Auth suggestion – blur overlay when shown; shows after 10s if auth still red */}
      <div id="authRequiredPopup" className="auth-required-popup hidden">
        <div className="auth-popup-overlay" aria-hidden="true" />
        <div className="auth-popup-content">
          <div className="auth-popup-accent"></div>
          <div className="auth-popup-body">
            <div className="auth-popup-icon-wrap">
              <svg className="auth-popup-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/>
              </svg>
            </div>
            <h3 className="auth-popup-title">Connect to trade</h3>
            <p className="auth-popup-message">Log in to your Deriv account to place trades and access your balance.</p>
            <div className="auth-popup-actions">
              <button
                type="button"
                className="auth-popup-login-btn"
                onClick={() => document.getElementById('loginBtn')?.click()}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/>
                </svg>
                Log in
              </button>
              <a
                href="https://deriv.com/signup/"
                target="_blank"
                rel="noopener noreferrer"
                className="auth-popup-signup-btn"
              >
                Sign up
              </a>
              <button
                type="button"
                className="auth-popup-dismiss-btn"
                onClick={() => {
                  const popup = document.getElementById('authRequiredPopup');
                  if (popup) popup.classList.add('hidden');
                }}
              >
                Later
              </button>
            </div>
            <p className="auth-popup-signup-line">
              First time? No trading account.
            </p>
            <div className="auth-popup-video-section">
              <p className="auth-popup-video-title">How to create your account</p>
              {ACCOUNT_TUTORIAL_VIDEO_ID ? (
                <div className="auth-popup-video-wrap">
                  <iframe
                    title="How to create your Deriv account"
                    src={`https://www.youtube.com/embed/${ACCOUNT_TUTORIAL_VIDEO_ID}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="auth-popup-video-iframe"
                  />
                </div>
              ) : (
                <div className="auth-popup-video-placeholder">
                  <span className="auth-popup-video-placeholder-icon">▶</span>
                  <span>Add your YouTube video ID in App.js (ACCOUNT_TUTORIAL_VIDEO_ID) to show a tutorial here.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop: layout with market list sidebar on extreme left */}
      <div className="app-desktop-layout">
        <aside id="marketListSidebar" className="market-list-sidebar desktop-only" aria-label="Markets" />
        <div className="app-main-content">
      {/* Controls Bar */}
      <div className="controls">
        {/* Symbol Selector - hidden on desktop when sidebar is shown */}
        <div className="control-group symbol-select-group">
          <select id="symbolSelect" title="Symbol" defaultValue="">
            <option value="">Loading...</option>
          </select>
        </div>
        
        <div className="control-group separator"></div>
        
        {/* Timeframe Buttons */}
        <button className="timeframe-btn" data-granularity="1" title="1 second">1s</button>
        <button className="timeframe-btn active" data-granularity="5" title="5 seconds">5s</button>
        <button className="timeframe-btn" data-granularity="10" title="10 seconds">10s</button>
        <button className="timeframe-btn" data-granularity="15" title="15 seconds">15s</button>
        <button className="timeframe-btn" data-granularity="30" title="30 seconds">30s</button>
        <button className="timeframe-btn" data-granularity="45" title="45 seconds">45s</button>
        <button className="timeframe-btn" data-granularity="60" title="M1 - 1 minute">M1</button>
        <button className="timeframe-btn" data-granularity="300" title="M5 - 5 minutes">M5</button>
        <button className="timeframe-btn" data-granularity="900" title="M15 - 15 minutes">M15</button>
        <button className="timeframe-btn" data-granularity="1800" title="M30 - 30 minutes">M30</button>
        <button className="timeframe-btn" data-granularity="3600" title="H1 - 1 hour">H1</button>
        <button className="timeframe-btn" data-granularity="14400" title="H4 - 4 hours">H4</button>
        <button className="timeframe-btn" data-granularity="86400" title="D1 - 1 day">D1</button>
        
        {/* More Timeframes Dropdown */}
        <div className="control-group">
          <select id="moreTimeframesDropdown" className="more-timeframes-dropdown" title="More Timeframes" defaultValue="">
            <option value="">More...</option>
            <option value="2">2 seconds</option>
            <option value="3">3 seconds</option>
            <option value="20">20 seconds</option>
            <option value="25">25 seconds</option>
            <option value="35">35 seconds</option>
            <option value="40">40 seconds</option>
            <option value="50">50 seconds</option>
            <option value="55">55 seconds</option>
            <option value="120">2 minutes</option>
            <option value="180">3 minutes</option>
            <option value="600">10 minutes</option>
            <option value="7200">2 hours</option>
            <option value="28800">8 hours</option>
          </select>
        </div>
        
        {/* Hidden granularity select */}
        <select id="granularitySelect" style={{display: 'none'}} defaultValue="5">
          <option value="1">1 second</option>
          <option value="2">2 seconds</option>
          <option value="3">3 seconds</option>
          <option value="5">5 seconds</option>
          <option value="10">10 seconds</option>
          <option value="15">15 seconds</option>
          <option value="20">20 seconds</option>
          <option value="25">25 seconds</option>
          <option value="30">30 seconds</option>
          <option value="35">35 seconds</option>
          <option value="40">40 seconds</option>
          <option value="45">45 seconds</option>
          <option value="50">50 seconds</option>
          <option value="55">55 seconds</option>
          <option value="60">60 seconds (1 minute)</option>
          <option value="120">2 minutes</option>
          <option value="180">3 minutes</option>
          <option value="300">5 minutes</option>
          <option value="600">10 minutes</option>
          <option value="900">15 minutes</option>
          <option value="1800">30 minutes</option>
          <option value="3600">1 hour</option>
          <option value="7200">2 hours</option>
          <option value="14400">4 hours</option>
          <option value="28800">8 hours</option>
          <option value="86400">1 day</option>
        </select>
        
        <div className="control-group separator"></div>
        
        {/* Chart Type Toggle */}
        <button id="candleTypeBtn" className="icon-btn" title="Candle Type: Heikin Ashi">📊</button>
        
        <div className="control-group separator"></div>
        
        {/* Settings and Theme */}
        <button id="settingsBtn" className="icon-btn" title="Settings">⚙️</button>
        <button id="themeToggle" className="icon-btn" title="Toggle Theme">🌓</button>
        
        <div className="control-group separator"></div>
        
        {/* Account Selection and Balance */}
        <div className="control-group">
          <div className="account-toggle-wrapper">
            <label className="account-toggle-switch" id="accountToggle" title="Toggle between Real and Demo Account">
              <input type="checkbox" id="accountToggleInput" />
              <span className="account-toggle-slider">
                <span className="account-toggle-thumb"></span>
              </span>
              <span className="account-toggle-label" id="accountToggleLabel">Real</span>
            </label>
          </div>
        </div>
        
        <div className="control-group">
          <span id="accountName" className="account-name" title="Account Name">-</span>
        </div>
        
        <div className="control-group balance-box-wrap">
          <span id="balanceDisplay" className="balance-display" title="Balance">$0.00</span>
        </div>
        
        <div className="control-group separator"></div>
        
        {/* Status and Login */}
        <div className="status-group">
          <button id="loginBtn" className="login-btn" title="Login">
            <span className="login-avatar-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                <path d="M12.0002 14.5C6.99016 14.5 2.91016 17.86 2.91016 22C2.91016 22.28 3.13016 22.5 3.41016 22.5H20.5902C20.8702 22.5 21.0902 22.28 21.0902 22C21.0902 17.86 17.0102 14.5 12.0002 14.5Z" fill="currentColor"/>
              </svg>
            </span>
            <span className="login-btn-text">Login</span>
          </button>
          <span id="authStatus" className="status disconnected" title="Authorization Status">Auth</span>
          <span id="status" className="status disconnected">Connecting...</span>
        </div>
      </div>
      
      {/* Settings Modal */}
      <div id="settingsModal" className="settings-modal">
        <div className="settings-modal-content">
          <div className="settings-modal-header">
            <h2>Settings</h2>
            <button id="closeSettingsBtn" className="settings-close-btn" title="Close">×</button>
          </div>
          <div className="settings-modal-body">
            <div className="settings-section">
              <h3>Candle Colors</h3>
              <div className="settings-color-group">
                <div className="settings-color-item">
                  <label>Bullish Candle</label>
                  <input type="color" id="bullishColorPicker" title="Bullish Candle Color" />
                </div>
                <div className="settings-color-item">
                  <label>Bearish Candle</label>
                  <input type="color" id="bearishColorPicker" title="Bearish Candle Color" />
                </div>
              </div>
            </div>
            <div className="settings-section">
              <h3>Moving Average (MA5)</h3>
              <div className="settings-color-group">
                <div className="settings-color-item">
                  <label>MA5 Color</label>
                  <input type="color" id="ma5ColorPicker" title="MA5 Color" defaultValue="#000080" />
                </div>
                <div className="settings-color-item">
                  <label>MA Period</label>
                  <input 
                    type="number" 
                    id="ma5PeriodInput" 
                    title="MA Period" 
                    defaultValue="5" 
                    min="1" 
                    max="100" 
                    style={{
                      width: '80px',
                      padding: '4px 8px',
                      height: '32px',
                      backgroundColor: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-primary)',
                      borderRadius: '2px',
                      fontSize: '11px',
                      fontFamily: "'Arial', 'Tahoma', 'Verdana', sans-serif"
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="settings-section">
              <h3>Trading History</h3>
              <div className="settings-color-group">
                <div className="settings-color-item" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                  <label>Show Entry/Exit Points</label>
                  <label className="toggle-switch">
                    <input type="checkbox" id="tradingHistoryToggle" defaultChecked />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
            <div className="settings-section">
              <h3>Momentum Indicator</h3>
              <div className="settings-color-group">
                <div className="settings-color-item">
                  <label>Min Streak Length</label>
                  <select id="minStreakSelect" style={{minWidth: '100px', padding: '4px 8px', height: '32px'}} defaultValue="3">
                    <option value="1">1+</option>
                    <option value="2">2+</option>
                    <option value="3">3+</option>
                    <option value="4">4+</option>
                    <option value="5">5+</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="settings-modal-footer">
              <a href={NEPHTECH_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="settings-nephtech-link" title="Contact Nephtech on WhatsApp">
                <svg className="nephtech-whatsapp-icon" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <span>Designed by Nephtech</span>
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Chart area + right icon nav (desktop: nav beside chart) */}
      <div className="chart-area-with-right-nav">
        <div className="chart-column">
      {/* Indicators Ribbon */}
      <div className="indicators-ribbon">
        <button id="momentumIndicatorBtn" className="indicator-btn" title="Momentum Indicator">📈 Momentum</button>
        <button id="cloudToggleBtn" className="indicator-btn" title="Momentum Cloud" data-active="false">☁️ Cloud</button>
        <button id="ma5ToggleBtn" className="indicator-btn" title="5-Period Moving Average" data-active="false">📊 MA5</button>
        {/* Zoom and Drawing controls for mobile */}
        <div className="chart-zoom-controls mobile-controls-group">
          <button id="zoomOutBtnMobile" className="zoom-btn" title="Zoom Out">−</button>
          <button id="resetZoomBtnMobile" className="zoom-btn" title="Reset Zoom">⊙</button>
          <button id="zoomInBtnMobile" className="zoom-btn" title="Zoom In">+</button>
        </div>
        <div className="drawing-ribbon mobile-controls-group">
          <button id="drawingNoneBtnMobile" className="drawing-btn active" data-mode="none" title="None">None</button>
          <button id="drawingLineBtnMobile" className="drawing-btn" data-mode="line" title="Line">Line</button>
          <button id="drawingRectBtnMobile" className="drawing-btn" data-mode="rectangle" title="Rectangle">Rect</button>
          <button id="clearDrawingsBtnMobile" className="drawing-btn" title="Clear All Drawings" style={{display: 'none'}}>Clear</button>
        </div>
      </div>
      
      {/* Desktop right-nav views: chart | signals | community (only one visible on desktop) */}
      <div className="right-nav-views">
        <div className={`chart-view-panel ${activeRightNavView === 'chart' ? 'active' : ''}`}>
      {/* Main Content Area - switches based on mobile page */}
      <div className={`mobile-page-container ${activeMobilePage}`}>
        
        {/* Chart Page */}
        <div id="chart" className={`mobile-page ${activeMobilePage === 'chart' ? 'active' : ''}`}>
          <div className="chart-background-image"></div>
          <div className="chart-watermark">
            <div className="chart-watermark-logo"><span className="brand-accent">G</span>letradershub.com</div>
            <div className="chart-watermark-tagline">
              <a href={NEPHTECH_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="chart-watermark-nephtech" title="Contact Nephtech on WhatsApp">
              </a>
            </div>
          </div>
          <div className="chart-loading" id="chartLoading">
            <div className="bar-graph-container">
              <div className="bar-graph-bar"></div>
              <div className="bar-graph-bar"></div>
              <div className="bar-graph-bar"></div>
              <div className="bar-graph-bar"></div>
              <div className="bar-graph-bar"></div>
              <div className="bar-graph-bar"></div>
              <div className="bar-graph-bar"></div>
              <div className="bar-graph-bar"></div>
              <div className="bar-graph-bar"></div>
              <div className="bar-graph-bar"></div>
            </div>
            <div className="loading-text">Loading chart data...</div>
          </div>
          <div className="chart-zoom-controls desktop-controls-group">
            <button id="zoomOutBtn" className="zoom-btn" title="Zoom Out">−</button>
            <button id="resetZoomBtn" className="zoom-btn" title="Reset Zoom">⊙</button>
            <button id="zoomInBtn" className="zoom-btn" title="Zoom In">+</button>
          </div>
          
          {/* Trading Widget */}
          <div className="trading-widget">
            {/* Quick Stake Buttons */}
            <div className="quick-stake-buttons">
              <button className="quick-stake-btn" id="doubleStakeBtn" data-action="double">X2</button>
              <button className="quick-stake-btn" data-stake="10">10</button>
              <button className="quick-stake-btn" data-stake="50">50</button>
              <button className="quick-stake-btn" data-stake="100">100</button>
              <button className="quick-stake-btn" data-stake="200">200</button>
              <button className="quick-stake-btn" data-stake="500">500</button>
              <button className="quick-stake-btn" data-stake="1000">1K</button>
              <button className="quick-stake-btn" data-stake="2000">2K</button>
              <button className="quick-stake-btn" data-stake="3000">3K</button>
              <button className="quick-stake-btn" data-stake="5000">5K</button>
            </div>
            <div className="trading-widget-quantity">
              <button className="quantity-btn quantity-down" id="quantityDownBtn">▼</button>
              <input type="text" id="quantityInput" className="quantity-input" defaultValue="100 USD" />
              <button className="quantity-btn quantity-up" id="quantityUpBtn">▲</button>
            </div>
            <div className="trading-widget-duration">
              <select id="durationTypeSelect" className="duration-type-select" defaultValue="seconds">
                <option value="seconds">Seconds</option>
                <option value="minutes">Minutes</option>
              </select>
              <button className="quantity-btn quantity-down" id="durationDownBtn">▼</button>
              <input type="text" id="durationInput" className="quantity-input duration-input" defaultValue="30" />
              <button className="quantity-btn quantity-up" id="durationUpBtn">▲</button>
            </div>
            <div className="trading-widget-panels">
              <div className="trading-panel sell-panel disabled" id="sellPanel">
                <div className="panel-label">SELL</div>
                <div className="panel-price">
                  <span className="price-main" id="sellPriceMain">1.17</span>
                  <span className="price-superscript" id="sellPriceSup">69</span>
                  <span className="price-exponent" id="sellPriceExp">⁸</span>
                </div>
              </div>
              <div className="trading-panel buy-panel disabled" id="buyPanel">
                <div className="panel-label">BUY</div>
                <div className="panel-price">
                  <span className="price-main" id="buyPriceMain">1.17</span>
                  <span className="price-superscript" id="buyPriceSup">70</span>
                  <span className="price-exponent" id="buyPriceExp">¹</span>
                </div>
              </div>
            </div>
            {/* Mobile-only layout */}
            <div className="trading-widget-main mobile-only">
              <div className="trading-panel sell-panel disabled" id="sellPanelMobile">
                <div className="panel-label">SELL</div>
                <div className="panel-price-full" id="sellPriceFull">0.00</div>
              </div>
              <div className="trading-widget-center-input">
                <input type="text" id="quantityInputMobile" className="quantity-input-center" defaultValue="100 USD" />
              </div>
              <div className="trading-panel buy-panel disabled" id="buyPanelMobile">
                <div className="panel-label">BUY</div>
                <div className="panel-price-full" id="buyPriceFull">0.00</div>
              </div>
            </div>
            {/* Mobile-only duration input */}
            <div className="trading-widget-duration-mobile mobile-only">
              <select id="durationTypeSelectMobile" className="duration-type-select" defaultValue="seconds">
                <option value="seconds">Seconds</option>
                <option value="minutes">Minutes</option>
              </select>
              <button className="quantity-btn quantity-down" id="durationDownBtnMobile">▼</button>
              <input type="text" id="durationInputMobile" className="quantity-input duration-input" defaultValue="30" />
              <button className="quantity-btn quantity-up" id="durationUpBtnMobile">▲</button>
            </div>
          </div>
          
          {/* Open Contracts Table - Desktop only (floating) */}
          <div className="open-contracts-container desktop-only" id="openContractsContainer">
            <div className="open-contracts-header">
              <div className="contracts-tabs">
                <button className="contract-tab active" data-tab="open" id="openTabBtn">Open</button>
                <button className="contract-tab" data-tab="history" id="historyTabBtn">History</button>
              </div>
              <div className="contracts-controls">
                <button id="maximizeContractsBtn" className="maximize-contracts-btn" title="Maximize">⛶</button>
                <button id="toggleContractsBtn" className="toggle-contracts-btn" title="Toggle Contracts">▼</button>
              </div>
            </div>
            <div className="open-contracts-table-wrapper" id="openContractsTableWrapper">
              {/* Open Contracts Tab */}
              <div className="contracts-tab-content active" id="openTabContent">
                <table className="open-contracts-table" id="openContractsTable">
                  <thead>
                    <tr>
                      <th>Symbol</th>
                      <th>Type</th>
                      <th>Entry</th>
                      <th>Current</th>
                      <th>P/L</th>
                      <th>Remaining</th>
                    </tr>
                  </thead>
                  <tbody id="openContractsTableBody">
                    <tr className="no-contracts">
                      <td colSpan="6">No open trades</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              {/* Contract History Tab */}
              <div className="contracts-tab-content" id="historyTabContent">
                <table className="open-contracts-table" id="contractHistoryTable">
                  <thead>
                    <tr>
                      <th>Symbol</th>
                      <th>Type</th>
                      <th>Entry</th>
                      <th>Exit</th>
                      <th>P/L</th>
                      <th>Duration <button id="clearHistoryBtn" className="clear-history-x-btn" title="Clear History">×</button></th>
                    </tr>
                  </thead>
                  <tbody id="contractHistoryTableBody">
                    <tr className="no-contracts">
                      <td colSpan="6">No contract history</td>
                    </tr>
                  </tbody>
                  <tfoot className="history-total-row">
                    <tr>
                      <td colSpan="4" className="history-total-label">Total P/L</td>
                      <td id="historyTotalPL" colSpan="2" className="history-total-value">$0.00</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
          
          <div className="drawing-ribbon desktop-controls-group">
            <button id="drawingNoneBtn" className="drawing-btn active" data-mode="none" title="None">None</button>
            <button id="drawingLineBtn" className="drawing-btn" data-mode="line" title="Line">Line</button>
            <button id="drawingRectBtn" className="drawing-btn" data-mode="rectangle" title="Rectangle">Rect</button>
            <button id="clearDrawingsBtn" className="drawing-btn" title="Clear All Drawings" style={{display: 'none'}}>Clear</button>
          </div>
        </div>

        {/* Open Trades Page - Mobile Only */}
        <div className={`mobile-page mobile-trades-page ${activeMobilePage === 'trades' ? 'active' : ''}`}>
          <div className="mobile-page-header">
            <h2>Open Trades</h2>
          </div>
          <div className="mobile-page-content">
            <table className="open-contracts-table mobile-full-table" id="mobileOpenContractsTable">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Type</th>
                  <th>Entry</th>
                  <th>Current</th>
                  <th>P/L</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody id="mobileOpenContractsTableBody">
                <tr className="no-contracts">
                  <td colSpan="6">No open trades</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* History Page - Mobile Only */}
        <div className={`mobile-page mobile-history-page ${activeMobilePage === 'history' ? 'active' : ''}`}>
          <div className="mobile-page-header">
            <h2>Trade History</h2>
            <button id="mobileClearHistoryBtn" className="clear-history-btn" title="Clear History">Clear</button>
          </div>
          <div className="mobile-page-content">
            <table className="open-contracts-table mobile-full-table" id="mobileContractHistoryTable">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Type</th>
                  <th>Entry</th>
                  <th>Exit</th>
                  <th>P/L</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody id="mobileContractHistoryTableBody">
                <tr className="no-contracts">
                  <td colSpan="6">No contract history</td>
                </tr>
              </tbody>
            </table>
            <div className="mobile-total-pl">
              Total P/L: <span id="mobileTotalPL" className="total-pl-value">$0.00</span>
            </div>
          </div>
        </div>

        {/* Settings Page - Mobile Only */}
        <div className={`mobile-page mobile-settings-page ${activeMobilePage === 'settings' ? 'active' : ''}`}>
          <div className="mobile-page-header">
            <h2>Settings</h2>
          </div>
          <div className="mobile-page-content mobile-settings-content">
            <div className="mobile-settings-section">
              <h3>Account</h3>
              <div className="mobile-settings-item">
                <span className="mobile-settings-label">Account Type</span>
                <div className="account-toggle-wrapper">
                  <label className="account-toggle-switch" title="Toggle between Real and Demo Account">
                    <input type="checkbox" id="mobileAccountToggleInput" />
                    <span className="account-toggle-slider">
                      <span className="account-toggle-thumb"></span>
                    </span>
                    <span className="account-toggle-label" id="mobileAccountToggleLabel">Real</span>
                  </label>
                </div>
              </div>
              <div className="mobile-settings-item">
                <span className="mobile-settings-label">Balance</span>
                <span id="mobileBalanceDisplay" className="mobile-settings-value">$0.00</span>
              </div>
            </div>

            <div className="mobile-settings-section">
              <h3>Theme</h3>
              <div className="mobile-settings-item">
                <span className="mobile-settings-label">Dark Mode</span>
                <label className="toggle-switch">
                  <input type="checkbox" id="mobileThemeToggle" defaultChecked />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="mobile-settings-section">
              <h3>Chart</h3>
              <div className="mobile-settings-item">
                <span className="mobile-settings-label">Candle Type</span>
                <select id="mobileCandleTypeSelect" className="mobile-settings-select" defaultValue="heikin">
                  <option value="heikin">Heikin Ashi</option>
                  <option value="regular">Regular</option>
                </select>
              </div>
              <div className="mobile-settings-item">
                <span className="mobile-settings-label">Bullish Color</span>
                <input type="color" id="mobileBullishColorPicker" className="mobile-color-picker" />
              </div>
              <div className="mobile-settings-item">
                <span className="mobile-settings-label">Bearish Color</span>
                <input type="color" id="mobileBearishColorPicker" className="mobile-color-picker" />
              </div>
            </div>

            <div className="mobile-settings-section">
              <h3>Indicators</h3>
              <div className="mobile-settings-item">
                <span className="mobile-settings-label">MA5</span>
                <label className="toggle-switch">
                  <input type="checkbox" id="mobileMa5Toggle" defaultChecked />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="mobile-settings-item">
                <span className="mobile-settings-label">MA5 Color</span>
                <input type="color" id="mobileMa5ColorPicker" className="mobile-color-picker" defaultValue="#000080" />
              </div>
              <div className="mobile-settings-item">
                <span className="mobile-settings-label">Show Trade History</span>
                <label className="toggle-switch">
                  <input type="checkbox" id="mobileTradingHistoryToggle" defaultChecked />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="mobile-settings-section">
              <button id="mobileLoginBtn" className="mobile-login-btn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                  <path d="M12.0002 14.5C6.99016 14.5 2.91016 17.86 2.91016 22C2.91016 22.28 3.13016 22.5 3.41016 22.5H20.5902C20.8702 22.5 21.0902 22.28 21.0902 22C21.0902 17.86 17.0102 14.5 12.0002 14.5Z" fill="currentColor"/>
                </svg>
                Login / Logout
              </button>
            </div>
        </div>
      </div>
      </div>
        </div>
        {/* Signals view - desktop right nav */}
        <div className={`signals-view-panel desktop-only ${activeRightNavView === 'signals' ? 'active' : ''}`}>
          <div className="right-nav-page-content">
            <h2 className="right-nav-page-title">Signals</h2>
            <p className="right-nav-page-desc">Trading signals and copy trading.</p>
          </div>
        </div>
        {/* Community view - desktop right nav */}
        <div className={`community-view-panel desktop-only ${activeRightNavView === 'community' ? 'active' : ''}`}>
          <div className="right-nav-page-content">
            <h2 className="right-nav-page-title">Community</h2>
            <p className="right-nav-page-desc">Discuss and share with the community.</p>
          </div>
        </div>
      </div>
    </div>

      {/* Right icon nav - desktop only: Chart, Signals, Community (sibling of chart-column so it sits beside chart) */}
      <aside className="right-icon-nav desktop-only" aria-label="Tools">
        <button
          type="button"
          className={`right-nav-icon-btn ${activeRightNavView === 'chart' ? 'active' : ''}`}
          title="Chart"
          data-nav="chart"
          onClick={() => setActiveRightNavView('chart')}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="13" width="4" height="8" rx="1"/><rect x="10" y="9" width="4" height="12" rx="1"/><rect x="17" y="5" width="4" height="16" rx="1"/>
          </svg>
        </button>
        <button
          type="button"
          className={`right-nav-icon-btn ${activeRightNavView === 'signals' ? 'active' : ''}`}
          title="Signals"
          data-nav="signals"
          onClick={() => setActiveRightNavView('signals')}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
          </svg>
        </button>
        <button
          type="button"
          className={`right-nav-icon-btn ${activeRightNavView === 'community' ? 'active' : ''}`}
          title="Community"
          data-nav="community"
          onClick={() => setActiveRightNavView('community')}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        </button>
        <div className="right-icon-nav-logo">
          <img src="/logo/logo.png" alt="gletradershub.com" />
          <a href={NEPHTECH_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="right-nav-nephtech" title="Contact Nephtech on WhatsApp">
            <span className="nephtech-copyright">Designed by Nephtech</span>
            <svg className="nephtech-whatsapp-icon" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </a>
        </div>
      </aside>
    </div>

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-bottom-nav">
        <button 
          className={`mobile-nav-item ${activeMobilePage === 'chart' ? 'active' : ''}`}
          onClick={() => setActiveMobilePage('chart')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="13" width="4" height="8" rx="1" fill="currentColor"/>
            <rect x="10" y="9" width="4" height="12" rx="1" fill="currentColor"/>
            <rect x="17" y="5" width="4" height="16" rx="1" fill="currentColor"/>
          </svg>
          <span>Chart</span>
        </button>
        <button 
          className={`mobile-nav-item ${activeMobilePage === 'trades' ? 'active' : ''}`}
          onClick={() => setActiveMobilePage('trades')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 6H21M3 12H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="19" cy="6" r="3" fill="#4CAF50"/>
          </svg>
          <span>Trade</span>
        </button>
        <button 
          className={`mobile-nav-item ${activeMobilePage === 'history' ? 'active' : ''}`}
          onClick={() => setActiveMobilePage('history')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 7V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span>History</span>
        </button>
        <button 
          className={`mobile-nav-item ${activeMobilePage === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveMobilePage('settings')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 1V3M12 21V23M4.22 4.22L5.64 5.64M18.36 18.36L19.78 19.78M1 12H3M21 12H23M4.22 19.78L5.64 18.36M18.36 5.64L19.78 4.22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span>Settings</span>
        </button>
      </nav>
      <a href={NEPHTECH_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="mobile-nephtech-bar" title="Contact Nephtech on WhatsApp">
        <span className="nephtech-copyright">Designed by Nephtech</span>
        <svg className="nephtech-whatsapp-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
        </div>
      </div>
    </>
  );
}

export default App;
